/**
 * Chat Context Provider
 * 
 * Provides contextual information about the user's current view to the chatbot.
 * Combines page/chart awareness with active filter state.
 */

import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useFilterContext, FilterContextType } from './FilterContext';
import { getPageFromRoute } from './pageRegistry';
import { ChatContext, PageInfo, ChartInfo, ActiveFilters, AvailableFilterOptions } from '../types/chatContext';

// Create the context
const ChatContextContext = createContext<ChatContext | null>(null);

/**
 * Hook to access chat context
 * Safe to use even outside the provider (returns null)
 */
export function useChatContext(): ChatContext | null {
  return useContext(ChatContextContext);
}

/**
 * Hook to access chat context with error if not available
 */
export function useChatContextRequired(): ChatContext {
  const context = useContext(ChatContextContext);
  if (!context) {
    throw new Error('useChatContextRequired must be used within a ChatContextProvider');
  }
  return context;
}

interface ChatContextProviderProps {
  children: ReactNode;
}

/**
 * Chat Context Provider
 * 
 * Wraps the chatbot to provide contextual information.
 * MUST be placed inside FilterContextProvider and Router.
 */
export const ChatContextProvider: React.FC<ChatContextProviderProps> = ({ children }) => {
  const location = useLocation();
  
  // This is always available since we're inside FilterContextProvider
  const filterContext: FilterContextType = useFilterContext();
  
  // Get current page definition from route
  const pageDefinition = useMemo(() => {
    return getPageFromRoute(location.pathname);
  }, [location.pathname]);
  
  // Build the chat context
  const chatContext: ChatContext = useMemo(() => {
    // Page info
    const page: PageInfo = {
      id: pageDefinition.id,
      name: pageDefinition.name,
      description: pageDefinition.description,
    };
    
    // Charts on current page (include transformation if defined)
    const charts: ChartInfo[] = pageDefinition.charts.map(chart => ({
      id: chart.id,
      name: chart.name,
      purpose: chart.purpose,
      ...(chart.transformation && { transformation: chart.transformation }),
    }));
    
    // Format date using local time (not UTC) to avoid date shifting
    // when local time is after midnight UTC (e.g., 7+ PM EST)
    const formatLocalDate = (date: Date | null): string | null => {
      if (!date) return null;
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };

    // Build filters from FilterContext
    const filters: ActiveFilters = {
      brands: filterContext.selectedBrands || [],
      platforms: filterContext.selectedPlatforms || [],
      pillars: filterContext.selectedPillars || [],
      dateRange: {
        start: formatLocalDate(filterContext.startDate),
        end: formatLocalDate(filterContext.endDate),
      },
    };
    
    // Build available options from FilterContext
    const availableOptions: AvailableFilterOptions = {
      brands: filterContext.allBrands || [],
      platforms: filterContext.availablePlatforms?.map((p: { id: string; name: string }) => p.name) || [],
      pillars: filterContext.availablePillars || [],
      dateRange: {
        start: formatLocalDate(filterContext.dataDateRange?.start ?? null) || '',
        end: formatLocalDate(filterContext.dataDateRange?.end ?? null) || '',
      },
    };
    
    return {
      page,
      filters,
      availableOptions,
      charts,
    };
  }, [pageDefinition, filterContext]);
  
  return (
    <ChatContextContext.Provider value={chatContext}>
      {children}
    </ChatContextContext.Provider>
  );
};

export default ChatContextProvider;
